import { read } from './vault.http'
import { isUuid } from './sourceReviews'
import axios from 'axios'
import { apiClient, toServiceError, type ManualCommandConfig } from '@/services/http/client'

export const verificationPath = '/clients/subject-verifications'
export const requestedActions = [
  'profile.read',
  'profile.update',
  'document.read',
  'document.download',
  'document.upload',
  'output.read',
  'output.download',
  'output.publish',
  'firm_access.manage',
  'client_switch.manage',
] as const
export interface RawSubject {
  id: string
  tenant_id: string
  customer_id: string
  subject_type: string
  period_basis: string
  period_value: string
  current_revision: number
  archived: boolean
}
export interface SubjectChoice {
  facts: RawSubject
  source_digest: string
}
export interface SubjectPage {
  items: SubjectChoice[]
  total: number
  limit: number
  offset: number
}
export interface EvidenceChoice {
  id: string
  name: string
  version: number
  digest: string | null
  available: boolean
}
export interface EvidencePage {
  items: EvidenceChoice[]
  total: number
  page: number
  page_size: number
}
export interface VerificationReceipt {
  request_id: string
  state: 'pending' | 'cancelled'
  ordinal: number
  authority_granted: false
}
export interface VerificationCommand {
  idempotency_key: string
  case_id: string
  expected_source_digest: string
  proposed_account_id: string
  declared_capacity: string
  requested_role: 'legal_controller' | 'representative'
  requested_actions: string[]
  declared_evidence: Array<{ evidence_id: string; digest: string }>
  acknowledge_unverified_request: true
}
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)
const keys = (value: Record<string, unknown>, names: string[]) =>
  Object.keys(value).length === names.length &&
  names.every(name => Object.prototype.hasOwnProperty.call(value, name))
const text = (value: unknown, max: number): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= max
const uuid = (value: unknown): value is string =>
  typeof value === 'string' && isUuid(value) && value === value.toLowerCase()
export const digest = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{64}$/.test(value)
const integer = (value: unknown, min: number, max = Number.MAX_SAFE_INTEGER): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max

export function validSubjects(
  value: unknown,
  firm: string,
  limit: number,
  offset: number
): value is SubjectPage {
  if (
    !uuid(firm) ||
    !integer(limit, 1, 100) ||
    !integer(offset, 0, 100000) ||
    !object(value) ||
    !keys(value, ['items', 'total', 'limit', 'offset']) ||
    !Array.isArray(value.items) ||
    !integer(value.total, 0) ||
    value.limit !== limit ||
    value.offset !== offset ||
    value.items.length !== Math.min(limit, Math.max(0, value.total - offset))
  )
    return false
  const ids: string[] = []
  for (const item of value.items) {
    if (
      !object(item) ||
      !keys(item, ['facts', 'source_digest']) ||
      !digest(item.source_digest) ||
      !object(item.facts)
    )
      return false
    const facts = item.facts
    if (
      !keys(facts, [
        'id',
        'tenant_id',
        'customer_id',
        'subject_type',
        'period_basis',
        'period_value',
        'current_revision',
        'archived',
      ]) ||
      !uuid(facts.id) ||
      facts.tenant_id !== firm ||
      !text(facts.customer_id, 100) ||
      facts.subject_type !== 'HUF' ||
      !text(facts.period_basis, 100) ||
      !text(facts.period_value, 20) ||
      !integer(facts.current_revision, 1, 2147483647) ||
      typeof facts.archived !== 'boolean'
    )
      return false
    ids.push(facts.id)
  }
  return (
    new Set(ids).size === ids.length && ids.every((id, index) => index === 0 || ids[index - 1] < id)
  )
}

export async function subjects(firm: string, limit: number, offset: number) {
  const value = await read<unknown>(`${verificationPath}/sources`, {
    limit,
    offset,
    subject_type: 'HUF',
  })
  if (!validSubjects(value, firm, limit, offset))
    throw new Error('Current original Tax subjects could not be confirmed. Refresh sources.')
  return value
}

/** Project existing authorized Document metadata; never trust it as legal evidence. */
export async function evidence(
  firm: string,
  page: number,
  pageSize: number
): Promise<EvidencePage> {
  if (!uuid(firm) || !integer(page, 1, 100000) || !integer(pageSize, 1, 100))
    throw new Error('A valid original evidence page is required')
  const value = await read<unknown>('/documents', { page, page_size: pageSize })
  if (
    !object(value) ||
    !keys(value, ['items', 'total', 'page', 'page_size']) ||
    !Array.isArray(value.items) ||
    !integer(value.total, 0) ||
    value.page !== page ||
    value.page_size !== pageSize ||
    value.items.length !== Math.min(pageSize, Math.max(0, value.total - (page - 1) * pageSize))
  )
    throw new Error(
      'Current evidence document references could not be confirmed. Refresh evidence.'
    )
  const seen = new Set<string>()
  const items = value.items.map(item => {
    if (
      !object(item) ||
      !uuid(item.id) ||
      item.tenant_id !== firm ||
      !text(item.name, 255) ||
      !integer(item.version, 1, 2147483647) ||
      !(item.file_hash === null || digest(item.file_hash)) ||
      !(item.deleted_at === null || typeof item.deleted_at === 'string') ||
      seen.has(item.id)
    )
      throw new Error(
        'Current evidence document references could not be confirmed. Refresh evidence.'
      )
    seen.add(item.id)
    return {
      id: item.id,
      name: item.name,
      version: item.version,
      digest: item.file_hash as string | null,
      available: item.deleted_at === null && digest(item.file_hash),
    }
  })
  return { items, total: value.total, page, page_size: pageSize }
}

export function validReceipt(value: unknown, request?: string): value is VerificationReceipt {
  return (
    object(value) &&
    keys(value, ['request_id', 'state', 'ordinal', 'authority_granted']) &&
    uuid(value.request_id) &&
    (!request || value.request_id === request) &&
    value.authority_granted === false &&
    ((value.state === 'pending' && value.ordinal === 1) ||
      (value.state === 'cancelled' && value.ordinal === 2))
  )
}
export const requestPath = (request: string) => {
  if (!uuid(request)) throw new Error('A valid request reference is required')
  return `${verificationPath}/${request}`
}
export async function ownReceipt(request: string) {
  const value = await read<unknown>(requestPath(request))
  if (!validReceipt(value, request))
    throw new Error('Current original request status could not be confirmed')
  return value
}
export async function commandReceipt(key: string) {
  if (!uuid(key)) throw new Error('A valid original command reference is required')
  const value = await read<unknown>(`${verificationPath}/by-command/${key}`)
  if (!validReceipt(value))
    throw new Error('Current original request status could not be confirmed')
  return value
}
export async function ownAccountReference(account: string) {
  const value = await read<unknown>('/auth/client/me')
  if (
    !object(value) ||
    !keys(value, ['user_id', 'scope', 'controllers']) ||
    value.user_id !== account ||
    !uuid(value.user_id) ||
    value.scope !== 'client_vault' ||
    !Array.isArray(value.controllers)
  )
    throw new Error('Your current independent account reference could not be confirmed')
  return value.user_id
}
export const validDeclaration = (
  account: string,
  capacity: string,
  role: string,
  actions: string[],
  documents: EvidenceChoice[]
) =>
  uuid(account) &&
  /^[A-Za-z][A-Za-z0-9._-]{0,79}$/.test(capacity) &&
  ['legal_controller', 'representative'].includes(role) &&
  actions.length >= 1 &&
  new Set(actions).size === actions.length &&
  actions.every(action => (requestedActions as readonly string[]).includes(action)) &&
  documents.length >= 1 &&
  documents.length <= 10 &&
  new Set(documents.map(item => item.id)).size === documents.length &&
  documents.every(item => uuid(item.id) && item.available && digest(item.digest))

export class VerificationNotRecorded extends Error {
  constructor(
    message: string,
    readonly key: string
  ) {
    super(message)
  }
}
/** Only exact current-key pre-insert rejections release a prospective draft. */
export async function submitVerification(payload: VerificationCommand) {
  try {
    const config: ManualCommandConfig = { manualCommand: true }
    const result = await apiClient.post<unknown>(verificationPath, payload, config)
    if (!validReceipt(result.data))
      throw new Error(
        'The original request receipt could not be confirmed. Check its command reference or retry the exact original request.'
      )
    return result.data
  } catch (failure) {
    if (axios.isAxiosError(failure) && object(failure.response?.data)) {
      const detail = failure.response.data.detail
      if (
        object(detail) &&
        keys(detail, [
          'code',
          'idempotency_key',
          'request_recorded',
          'authority_granted',
          'message',
        ]) &&
        detail.idempotency_key === payload.idempotency_key &&
        detail.request_recorded === false &&
        detail.authority_granted === false &&
        text(detail.message, 300) &&
        ((detail.code === 'SUBJECT_SOURCE_CHANGED' && failure.response?.status === 409) ||
          (detail.code === 'SUBJECT_INTAKE_DISABLED' && failure.response?.status === 503))
      )
        throw new VerificationNotRecorded(detail.message, payload.idempotency_key)
    }
    throw toServiceError(failure)
  }
}
export const referenceStorageKey = (account: string, firm: string) =>
  `TaxHive_subject_request_reference.v1:${account}:${firm}`
